import React, { useEffect, useState } from 'react';

import { formatAccountNumber } from '@eika-infrastruktur/format';
import { RowButton } from '@eika/button';
import { Icon, IconTypes } from '@eika/icon';
import { Label } from '@eika/label';
import { Loader } from '@eika/loader';
import { ModalDialogType } from '@eika/modal';

import useOcr from '../../../fetchHooks/useOcr';
import useReceivers from '../../../fetchHooks/useReceivers';
import useTranslation, { format } from '../../../hooks/useTranslation';
import { EikaModal } from '../../EikaModal';
import toSelectedReceiver from '../SelectedReceiver';
import { SelectReceiverForm } from './SelectReceiver';

import './ReceiverField.scss';

type SelectedReceiver = import('../SelectedReceiver').SelectedReceiver;

interface Props {
    receiver?: SelectedReceiver;
    onChange: (receiverAccount: SelectedReceiver) => void;
    error?: string;
    disabled?: boolean;
    onShow?: (show: boolean) => void;
    locked?: boolean;
    animateModalAs?: ModalDialogType;
}

const ReceiverField: React.FC<Props> = ({
    receiver,
    onChange,
    error,
    disabled,
    onShow,
    locked = false,
    animateModalAs,
}) => {
    const [showModal, setShowModal] = useState(false);
    const translated = useTranslation();
    const { data: receivers, ...receiverState } = useReceivers({ enabled: showModal });

    const triggerModal = (show: boolean) => {
        setShowModal(show);
        onShow?.(show);
    };

    const ocrQuery = useOcr(receiver?.accountNumber);

    useEffect(() => {
        if (!!receiver && receiver.isOCR === undefined && ocrQuery.isSuccess && ocrQuery.data) {
            onChange(
                toSelectedReceiver(
                    { accountNumber: receiver.accountNumber, name: receiver.accountName },
                    ocrQuery.data,
                ),
            );
        }
    }, [ocrQuery.data, ocrQuery.isSuccess, onChange, receiver, receiver?.accountNumber, receiver?.accountName]);

    return (
        <>
            <RowButton onClick={() => triggerModal(true)} disabled={disabled || locked} data-testid="to-account">
                {(receiver || error) && <div className="receiver-account-error-label">{translated.to}</div>}

                <RowButton.AccountSelectorLayout
                    placeholder={translated.addReceiver}
                    accountAlias={receiver?.accountName ?? formatAccountNumber(receiver?.accountNumber)}
                    accountNumber={receiver?.accountName ? formatAccountNumber(receiver.accountNumber) : ''}
                    error={error}
                    icon={locked ? <Icon type={IconTypes.PIN_KODE_24} ariaLabel="Låst" /> : IconTypes.LITEN_PIL_HOEYRE}
                />
                {ocrQuery.error && (
                    <div className="receiver-account-label">
                        <Label
                            text={format(translated.invalidToAccount, receiver?.accountNumber.value ?? '')}
                            type="error"
                            size="small"
                        />
                    </div>
                )}
                {receiver?.nameOverride && (
                    <div className="receiver-account-label">
                        <Label text={format(translated.receiverNameOverride, receiver?.nameOverride)} />
                    </div>
                )}
            </RowButton>
            <EikaModal
                id="receiverAccountField"
                open={showModal}
                setOpen={setShowModal}
                onClose={() => triggerModal(false)}
                title={translated.payTo}
                animateAs={animateModalAs}
            >
                {receiverState.isLoading ? (
                    <div className="sgw-center-loader">
                        <Loader />
                    </div>
                ) : (
                    <SelectReceiverForm
                        receivers={receivers}
                        onChange={(selectedReceiver) => {
                            triggerModal(false);
                            onChange({
                                accountNumber: selectedReceiver.accountNumber,
                                accountName: selectedReceiver.name,
                            });
                        }}
                        error={receiverState.error ?? undefined}
                        refresh={receiverState.refetch}
                    />
                )}
            </EikaModal>
        </>
    );
};

export default ReceiverField;
